import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IStoreState } from "../initialStoreState";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IJobs } from "./jobs_types";
import { uploadMultipleFile } from "../../components/FileUpload/utils";

export const FETCH_JOB_LIST_PROGRESS = "FETCH_JOB_LIST_PROGRESS";
export const FETCH_JOB_LIST_SUCCESS = "FETCH_JOB_LIST_SUCCESS";
export const FETCH_JOB_LIST_FAILED = "FETCH_JOB_LIST_FAILED";

export const fetchJobListProgress = () => action(FETCH_JOB_LIST_PROGRESS);
export const fetchJobListSuccess = (data: IJobs[], totalRecords: number) =>
  action(FETCH_JOB_LIST_SUCCESS, { data, totalRecords });
export const fetchJobListFailed = () => action(FETCH_JOB_LIST_FAILED);

export const fetchJobListAsync =
  (
    queryParams: IQueryParams,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchJobListProgress());
      let finalUrl = `/job/get-job${searchQuery}`;
      if (billing_company_uuid) {
        finalUrl += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(finalUrl);
      const data: IJobs[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchJobListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchJobListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertJobAsync =
  (
    job: IJobs,
    onCallback: (isSuccess: boolean, job?: IJobs) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        title: job.job_name,
      };

      const { paths } = await uploadMultipleFile(
        job.attachment || [],
        "JOB_BOARD",
        asPayload,
      );
      const {
        create_ts,
        insert_ts,
        created_by_name,
        requested_by_uuid,
        approval_uuid,
        is_user_approver,
        billing_company_branch_address_state,
        ...rest
      } = job;
      const res = await api.post("/job/upsert-job", {
        ...rest,
        attachment: paths,
      });
      const data = res.data.data;
      onCallback(true, data);
      dispatch(
        showMessage({
          type: "success",
          message: "PR saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_JOB_PROGRESS = "FETCH_JOB_PROGRESS";
export const FETCH_JOB_SUCCESS = "FETCH_JOB_SUCCESS";
export const FETCH_JOB_FAILED = "FETCH_JOB_FAILED";

export const fetchJobProgress = () => action(FETCH_JOB_PROGRESS);
export const fetchJobSuccess = (data: IJobs) =>
  action(FETCH_JOB_SUCCESS, { data });
export const fetchJobFailed = (errorMessage: string) =>
  action(FETCH_JOB_FAILED, { errorMessage });

export const fetchJobAsync =
  (
    jobUUID: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchJobProgress());
      let url = `/job/get-job?job_uuid=${jobUUID}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data: IJobs[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchJobSuccess(data[0]));
      } else {
        dispatch(fetchJobFailed("Oops! We couldn't find any records."));
      }
    } catch (err: any) {
      dispatch(fetchJobFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_JOB = "CLEAR_JOB";
export const CLEAR_JOB_STATE = "CLEAR_JOB_STATE";

export const clearJob = () => action(CLEAR_JOB);
export const clearJobState = () => action(CLEAR_JOB_STATE);

// ##########################################################################################
// ############################ Approval List#################################
// ##########################################################################################

export const FETCH_JOB_APPROVAL_LIST_PROGRESS =
  "FETCH_JOB_APPROVAL_LIST_PROGRESS";
export const FETCH_JOB_APPROVAL_LIST_SUCCESS =
  "FETCH_JOB_APPROVAL_LIST_SUCCESS";
export const FETCH_JOB_APPROVAL_LIST_FAILED = "FETCH_JOB_APPROVAL_LIST_FAILED";

export const fetchJobApprovalListProgress = () =>
  action(FETCH_JOB_APPROVAL_LIST_PROGRESS);
export const fetchJobApprovalListSuccess = (
  data: IJobs[],
  totalRecords: number,
) =>
  action(FETCH_JOB_APPROVAL_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchJobApprovalListFailed = () =>
  action(FETCH_JOB_APPROVAL_LIST_FAILED);

export const fetchJobApprovalListAsync =
  (
    queryParams: IQueryParams,
    table_name: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchJobApprovalListProgress());
      let searchQuery = getSearchQuery(queryParams);
      if (billing_company_uuid) {
        searchQuery += `&billing_company_uuid=${billing_company_uuid}`;
      }
      let finalUrl = `/approval/get-approval${searchQuery}&table_name=${table_name}`;
      const res = await api.get(finalUrl);
      const data: IJobs[] = res.data.data;
      dispatch(fetchJobApprovalListSuccess(data, res.data.totalRecords));
    } catch (err: any) {
      dispatch(fetchJobApprovalListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };
